import { Injectable, Injector, ComponentRef } from '@angular/core';
import { ApiService } from '@quorum/api';
import { TwoActionDialogComponent, TwoActionDialogData } from '@quorum/sha-components-ng-ui';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, combineLatest, Subject, ReplaySubject, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Overlay, OverlayRef, OverlayConfig, ConnectionPositionPair } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { BodyReportDamageComponent } from './body-report-damage/body-report-damage.component';
import { BodyReportDamageOverlayRef } from './body-report-damage/body-report-damage-ref';
import { BODY_REPORT_DAMAGE_DATA } from './body-report-damage/body-report-damage.tokens';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { SvcVehicleDiagnosticsRootFacade } from '@quorum/svc-vehicle-diagnostics-root';
import { GMServiceVisitVehicleData } from '@quorum/models/oem-resource';
import {
  OemRooftopLink,
  SystemControlValue,
  VehicleVipInspectionCategory,
  VehicleVipInspectionTask,
  WorkOrder,
} from '@quorum/models/xs-resource';
import {
  SystemControlValueQueryParameters,
  VehicleTireTreadQueryParameters,
  VehicleBrakeMeasurementQueryParameters,
  VehicleInspectionTypeQueryParameters,
  VehicleInspectionThresholdQueryParameters,
} from '@quorum/models/xs-query';

interface BodyReportDamageDialogConfig {
  panelClass?: string;
  hasBackdrop?: boolean;
  backdropClass?: string;
  selectedElement?: any;
  originElement?: any;
}

const DEFAULT_CONFIG: BodyReportDamageDialogConfig = {
  hasBackdrop: true,
  backdropClass: 'dark-backdrop',
  panelClass: 'tm-file-preview-dialog-panel',
  selectedElement: null,
  originElement: null,
};

//The name is the contract: the API identifies this category the same way.
export const DUAL_REAR_TIRES_CATEGORY_NAME = 'Dual Rear Tires';

//Driver-rear and passenger-rear positions within tires[0].tasks. The Tires card is hard-coded
//against these indexes, so the toggle must use the same ones or it would omit a rendered task.
const REAR_TIRE_TASK_INDEXES: readonly number[] = [1, 3];

//Inspections printed onto a predefined OEM PDF
const OEM_PDF_INSPECTION_NAMES: readonly string[] = [
  'GM MPVI - Standard',
  'GM MPVI - Express',
  'GM MPVI - Standard(EV)',
  'GM MPVI - Express(EV)',
  'Hyundai Vehicle Inspection Report (ICE)',
  'Hyundai Vehicle Inspection Report (EV)',
];
@Injectable({ providedIn: 'root' })
export class InspectionService {
  inspection: any;
  inspectionBehaviourSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  inspection$ = this.inspectionBehaviourSubject.asObservable();
  inspectionBackup: any = {};
  inspectionDetails: any;
  collapse: any = {};
  collapseWalkaround: any = {};
  collapseAll = true;
  tireTreads: any[] = [];
  brakeMeasurements: any[] = [];
  collapseWalkaroundAll = true;
  requiredTasks: any = {};
  walkAroundRequiredTasks: any = {};
  requiredCategory: any = {};
  walkAroundRequiredCategory: any = {};
  incomplete = false;
  walkAroundIncomplete = false;
  unsaved = false;
  //Two front plus four dually positions when true, the original two front / two rear layout
  //when false. Stored as the Omit state of the rear positions, not as a field of its own.
  dualRearTiresEnabled = false;
  treadDepthSettings: any = {};
  inspectionDeleteFlag: any;
  title: string;
  workOrderId: any;
  quoteId: any;
  isLoading: boolean;
  showButton: any;
  workOrderId$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  quoteId$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  preInspectionDataGathered$: Observable<any>;
  toastSubject = new Subject<any>();
  inspectionSavedNotification = new Subject<any>();
  triggerInspectionLogic: ReplaySubject<number> = new ReplaySubject<number>(1);
  vehicleId: number;
  vehicleDiagnosticsId: string;
  appointmentId: number;
  fuelType: string;
  bodyReportObject: any = {
    bodyReportElements: [],
    signature: null,
    vehicleDirty: false,
  };
  report: any = {
    header: { expressFlag: true },
  };
  dealershipLogo: string;
  inspectionTaskCount$: BehaviorSubject<{
    inspectionTabCategoryTotals: { [categoryName: string]: { actioned: number; omitted: number; total: number } };
    walkAroundTabCategoryTotals: { [categoryName: string]: { actioned: number; omitted: number; total: number } };
    totalInspectionTaskCount: number;
    totalInspectionActionedCount: number;
    totalInspectionOmittedCount: number;
  }> = new BehaviorSubject<{
    inspectionTabCategoryTotals: { [categoryName: string]: { actioned: number; omitted: number; total: number } };
    walkAroundTabCategoryTotals: { [categoryName: string]: { actioned: number; omitted: number; total: number } };
    totalInspectionTaskCount: number;
    totalInspectionActionedCount: number;
    totalInspectionOmittedCount: number;
  }>(null);
  brakeMeasurementTasks$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  filteredTasks$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private brakeThresholdCache: Map<string, any> = new Map();

  constructor(
    private apiService: ApiService,
    private dialog: MatDialog,
    private injector: Injector,
    private overlay: Overlay,
    private authenticationStateService: AuthenticationStateService,
    public svcVehicleDiagnosticsRootFacade: SvcVehicleDiagnosticsRootFacade
  ) {
    this.preInspectionDataGathered$ = combineLatest(
      this.workOrderId$.pipe(filter((woId) => woId != null)),
      this.quoteId$.pipe(filter((quoteId) => quoteId != null))
    ).pipe(
      map(([workOrderId, quoteId]) => {
        this.workOrderId = workOrderId;
        this.quoteId = quoteId;
        return { workOrderId, quoteId };
      })
    );
  }

  resetService() {
    this.brakeThresholdCache.clear();
    this.vehicleDiagnosticsId = null;
    this.inspection = null;
    this.inspectionBehaviourSubject.next(this.inspection);
    this.inspectionDetails = null;
    this.collapse = {};
    this.collapseWalkaround = {};
    this.collapseAll = true;
    this.tireTreads = [];
    this.brakeMeasurements = [];
    this.collapseWalkaroundAll = true;
    this.inspectionBackup = {};
    this.requiredTasks = {};
    this.walkAroundRequiredTasks = {};
    this.requiredCategory = {};
    this.walkAroundRequiredCategory = {};
    this.incomplete = false;
    this.walkAroundIncomplete = false;
    this.unsaved = false;
    this.dualRearTiresEnabled = false;
    this.treadDepthSettings = {};
    this.inspectionDeleteFlag = null;
    this.title = null;
    this.workOrderId = null;
    this.quoteId = null;
    this.isLoading = null;
    this.showButton = null;
    this.workOrderId$ = new BehaviorSubject<any>(null);
    this.quoteId$ = new BehaviorSubject<any>(null);
    this.report = {
      header: { expressFlag: true },
    };
    this.preInspectionDataGathered$ = combineLatest(
      this.workOrderId$.pipe(filter((woId) => woId != null)),
      this.quoteId$.pipe(filter((quoteId) => quoteId != null))
    ).pipe(
      map(([workOrderId, quoteId]) => {
        this.workOrderId = workOrderId;
        this.quoteId = quoteId;
        return { workOrderId, quoteId };
      })
    );
  }

  addToast(toastObject: any) {
    this.toastSubject.next(toastObject);
  }

  listenForToasts() {
    return this.toastSubject;
  }

  isCategoryWalkAround(category: any): boolean {
    return category.tasks.some((task: any) => task.walkAround === 1);
  }

  getDualRearTiresCategory(): VehicleVipInspectionCategory | null {
    const categories: VehicleVipInspectionCategory[] = this.inspection?.categories || [];
    return categories.find((category) => category.categoryName === DUAL_REAR_TIRES_CATEGORY_NAME) || null;
  }

  hasDualRearTires(): boolean {
    const category = this.getDualRearTiresCategory();
    return (category?.tasks?.length || 0) > 0;
  }

  //Inspections that print onto an OEM PDF get no toggle
  isDualRearTiresToggleSupported(): boolean {
    return this.hasDualRearTires() && !this.printsToOemPdf();
  }

  private printsToOemPdf(): boolean {
    return OEM_PDF_INSPECTION_NAMES.includes(this.inspection?.name);
  }

  initialiseDualRearTires(): void {
    if (!this.isDualRearTiresToggleSupported()) {
      this.dualRearTiresEnabled = false;
      return;
    }

    this.dualRearTiresEnabled = this.isCombinedLayoutPersisted();

    //Omit only untouched hidden tasks: leaving them unactioned makes savingCheck() block
    //completion, but clearing an existing assessment on load would lose captured work.
    this.getHiddenTireTasks().forEach((task) => {
      if (!this.isTaskAssessed(task) && !task.greyBox) {
        task.greyBox = true;
      }
    });
  }

  //Requiring an un-omitted dually position stops an advisor who omits both rear tires on an
  //ordinary inspection from being read as having asked for the combined layout.
  private isCombinedLayoutPersisted(): boolean {
    const standardRearTasks = this.getStandardRearTireTasks();

    if (standardRearTasks.length === 0) {
      return false;
    }

    return standardRearTasks.every((task) => task.greyBox) && this.getDualRearTireTasks().some((task) => !task.greyBox);
  }

  //Omit is what keeps hidden tasks out of the completion check, the counts, the report and
  //red-to-quote. Set here rather than via onTaskActionChange('grey'), which zeroes tread depth.
  setDualRearTiresEnabled(enabled: boolean): void {
    if (!this.isDualRearTiresToggleSupported()) {
      return;
    }

    this.dualRearTiresEnabled = enabled;

    this.getHiddenTireTasks().forEach((task) => {
      task.greyBox = true;
      task.greenBox = false;
      task.yellowBox = false;
      task.redBox = false;
    });

    this.getDisplayedRearTireTasks().forEach((task) => {
      if (task.greyBox) {
        task.greyBox = false;
      }
    });

    this.updateTaskCounts();
    this.inspectionBehaviourSubject.next(this.inspection);
  }

  //The Walk Around tab lists only walk-around tasks, so the standard rear cards may give way
  //there only when the dually positions carry that flag and will actually replace them.
  isCombinedLayoutVisibleOnWalkAround(): boolean {
    const category = this.getDualRearTiresCategory();
    return this.dualRearTiresEnabled && category != null && this.isCategoryWalkAround(category);
  }

  getHiddenTireTasks(): VehicleVipInspectionTask[] {
    if (!this.isDualRearTiresToggleSupported()) {
      return [];
    }
    return this.dualRearTiresEnabled ? this.getStandardRearTireTasks() : this.getDualRearTireTasks();
  }

  private getDisplayedRearTireTasks(): VehicleVipInspectionTask[] {
    return this.dualRearTiresEnabled ? this.getDualRearTireTasks() : this.getStandardRearTireTasks();
  }

  getDualRearTireTasks(): VehicleVipInspectionTask[] {
    return this.getDualRearTiresCategory()?.tasks || [];
  }

  getStandardRearTireTasks(): VehicleVipInspectionTask[] {
    const tasks: VehicleVipInspectionTask[] = this.inspection?.tires?.[0]?.tasks || [];
    return REAR_TIRE_TASK_INDEXES.map((index) => tasks[index]).filter((task) => task != null);
  }

  private isTaskAssessed(task: VehicleVipInspectionTask): boolean {
    return Boolean(task.greenBox || task.yellowBox || task.redBox);
  }

  getTireTreadValues(oemrtId: any) {
    let self = this;
    const vehicleTireTreadQueryParameters: VehicleTireTreadQueryParameters = new VehicleTireTreadQueryParameters({
      rooftopId: oemrtId,
    });
    return this.apiService
      .get('/v/2/vip/vehicle-tire-treads', {
        params: vehicleTireTreadQueryParameters,
      })
      .toPromise()
      .then(function (response: any) {
        var treadDetails = response;
        self.tireTreads = treadDetails;
        return treadDetails;
      });
  }

  getBrakeMeasurementValues(oemrtId: any) {
    let self = this;
    const brakeMeasurementQueryParameters: VehicleBrakeMeasurementQueryParameters =
      new VehicleBrakeMeasurementQueryParameters({
        rooftopId: oemrtId,
      });
    return this.apiService
      .get(`/v/2/vip/vehicle-brake-measurements`, {
        params: brakeMeasurementQueryParameters,
      })
      .toPromise()
      .then(function (response: any) {
        var brakeDetails = response;
        brakeDetails.forEach((brake: any) => {
          switch (brake.gmBrakeCode) {
            case 'G':
              brake.sequence = 3;
              break;
            case 'Y':
              brake.sequence = 2;
              break;
            case 'R':
              brake.sequence = 1;
              break;
            case '7':
              brake.sequence = 7;
              break;
            case '6':
              brake.sequence = 6;
              break;
            case '5':
              brake.sequence = 5;
              break;
            case '4':
              brake.sequence = 4;
              break;
            case '3':
              brake.sequence = 3;
              break;
            case '2':
              brake.sequence = 2;
              break;
            case '1':
              brake.sequence = 1;
              break;
          }
        });
        self.brakeMeasurements = brakeDetails;
        return brakeDetails;
      });
  }

  onTaskActionChange(color: any, task: any) {
    switch (color) {
      case 'green':
        task.greenBox = true;
        task.redBox = false;
        task.yellowBox = false;
        task.greyBox = false;
        break;
      case 'red':
        task.redBox = true;
        task.greenBox = false;
        task.yellowBox = false;
        task.greyBox = false;
        break;
      case 'yellow':
        task.yellowBox = true;
        task.redBox = false;
        task.greenBox = false;
        task.greyBox = false;
        break;
      case 'grey':
        task.greyBox = true;
        task.redBox = false;
        task.yellowBox = false;
        task.greenBox = false;
        if (task.gmInspectionId === 'TIRE_SEALANT_EXPIRATION') {
          task.vehicleSealantExpirationDate = null;
        }
        if (task.displayBrakeMeasurement) {
          task.brakeMeasurementId = null;
        }
        if (task.displayTireTread) {
          task.treadDepth = 0;
        }
        if (
          task.gmInspectionId === 'REMAININING_ENGINE_OIL_LIFE' ||
          task.inspectionId === 'Q_REMAININING_ENGINE_OIL_LIFE'
        ) {
          const categoryIndex = this.inspection.categories.findIndex(function (el: any) {
            return el.categoryId === task.categoryId;
          });
          if (categoryIndex != -1) {
            const taskIndex = this.inspection.categories[categoryIndex].tasks.findIndex(function (el: any) {
              return el.displayOilLife === true;
            });
            this.inspection.categories[categoryIndex].tasks[taskIndex].oilLife = 0;
            this.inspection.categories[categoryIndex].tasks[taskIndex].greyBox = true;
            this.inspection.categories[categoryIndex].tasks[taskIndex].redBox = false;
            this.inspection.categories[categoryIndex].tasks[taskIndex].yellowBox = false;
            this.inspection.categories[categoryIndex].tasks[taskIndex].greenBox = false;
          }
        }
        if (
          task.gmInspectionId === 'BATTERY_CONDITION' ||
          task.inspectionId === 'Q_BATTERY_CONDITION' ||
          task.inspectionId === 'HYU_BATTERY_CONDITION'
        ) {
          const categoryIndex = this.inspection.categories.findIndex(function (el: any) {
            return el.categoryId === task.categoryId;
          });

          if (categoryIndex != -1) {
            const taskIndex = this.inspection.categories[categoryIndex].tasks.findIndex(function (el: any) {
              return el.displayVoltage === true;
            });
            if (this.inspection.categories[categoryIndex].tasks[taskIndex]) {
              this.inspection.categories[categoryIndex].tasks[taskIndex].batteryVoltage = 0;
              this.inspection.categories[categoryIndex].tasks[taskIndex].greyBox = true;
              this.inspection.categories[categoryIndex].tasks[taskIndex].redBox = false;
              this.inspection.categories[categoryIndex].tasks[taskIndex].yellowBox = false;
              this.inspection.categories[categoryIndex].tasks[taskIndex].greenBox = false;
            }
          }
        }
        break;
      default:
        break;
    }
    this.updateTaskCounts();
    this.inspectionBehaviourSubject.next(this.inspection);
  }

  //Omit is the only control on a tire task, so it has to be able to undo itself
  toggleTaskOmit(task: VehicleVipInspectionTask): void {
    if (!task.greyBox) {
      this.onTaskActionChange('grey', task);
      return;
    }

    task.greyBox = false;
    task.greenBox = false;
    task.yellowBox = false;
    task.redBox = false;

    this.updateTaskCounts();
    this.inspectionBehaviourSubject.next(this.inspection);
  }

  onBrakeChange(task: any) {
    let brakeMeasurementValue: string | number;
    const index = this.brakeMeasurements.findIndex(function (el: any) {
      return el.id === task.brakeMeasurementId;
    });
    if (index === -1) {
      brakeMeasurementValue = index;
    } else {
      brakeMeasurementValue = this.brakeMeasurements[index].gmBrakeCode;
    }

    const inspectionTypeId = this.inspection.inspectionTypeId;
    const thresholdName = task.gmInspectionId ?? task.inspectionId;
    const thresholdKey = task.taskId != null ? `task:${task.taskId}` : `name:${thresholdName ?? 'none'}`;
    const cacheKey = `${inspectionTypeId}/${thresholdKey}`;

    const thresholdPromise = this.brakeThresholdCache.has(cacheKey)
      ? Promise.resolve(this.brakeThresholdCache.get(cacheKey))
      : this.getBrakeThreshold(inspectionTypeId, task.taskId, thresholdName).then((threshold: any) => {
          this.brakeThresholdCache.set(cacheKey, threshold);
          return threshold;
        });

    thresholdPromise
      .then((response: any) => {
        const brakeThresholds = response;
        const brakeMeasurement = parseInt(String(brakeMeasurementValue), 10);
        if (brakeThresholds?.greenThresholdValue && brakeMeasurement > 0) {
          if (brakeMeasurement >= brakeThresholds.greenThresholdValue) {
            task.greenBox = true;
            this.onTaskActionChange('green', task);
          } else if (brakeMeasurement >= brakeThresholds.yellowThresholdValue) {
            task.yellowBox = true;
            this.onTaskActionChange('yellow', task);
          } else {
            task.redBox = true;
            this.onTaskActionChange('red', task);
          }
        } else {
          this.applyBrakeCodeFallback(brakeMeasurementValue, task);
        }
        task.saveBrake = true;
        this.inspectionBehaviourSubject.next(this.inspection);
      })
      .catch(() => {
        this.applyBrakeCodeFallback(brakeMeasurementValue, task);
        task.saveBrake = true;
        this.inspectionBehaviourSubject.next(this.inspection);
      });
  }

  private getBrakeThreshold(
    inspectionTypeId: number,
    taskId: number | null | undefined,
    thresholdName?: string
  ): Promise<any> {
    if (taskId == null) {
      return thresholdName
        ? this.apiService.get(`/v/2/vip/vehicle-inspection-thresholds/${inspectionTypeId}/${thresholdName}`).toPromise()
        : Promise.resolve(null);
    }

    const queryParameters = new VehicleInspectionThresholdQueryParameters({
      vehicleInspectionTasksId: taskId,
    });

    return this.apiService
      .get(`/v/2/vip/vehicle-inspection-thresholds/${inspectionTypeId}`, {
        params: queryParameters,
      })
      .toPromise()
      .then((response: any) => {
        const taskThreshold = Array.isArray(response) ? response[0] : null;
        if (taskThreshold || !thresholdName) {
          return taskThreshold;
        }

        return this.apiService
          .get(`/v/2/vip/vehicle-inspection-thresholds/${inspectionTypeId}/${thresholdName}`)
          .toPromise();
      });
  }

  private applyBrakeCodeFallback(brakeMeasurementValue: any, task: any): void {
    const rearBrake = ['RR', 'Rear', 'rear', 'rr', 'LR', 'lr'];
    const rearBrakeFlag = rearBrake.some(function (el) {
      return task.taskName.includes(el);
    });
    if (rearBrakeFlag === false) {
      switch (brakeMeasurementValue) {
        case '7':
        case 'G':
          task.greenBox = true;
          this.onTaskActionChange('green', task);
          break;
        case '6':
        case '5':
        case '4':
        case 'Y':
          task.yellowBox = true;
          this.onTaskActionChange('yellow', task);
          break;
        case '3':
        case '2':
        case '1':
        case 'R':
          task.redBox = true;
          this.onTaskActionChange('red', task);
          break;
        default:
          this.onTaskActionChange('grey', task);
          break;
      }
    } else {
      switch (brakeMeasurementValue) {
        case '7':
        case '6':
        case '5':
        case '4':
        case 'G':
          task.greenBox = true;
          this.onTaskActionChange('green', task);
          break;
        case '3':
        case 'Y':
          task.yellowBox = true;
          this.onTaskActionChange('yellow', task);
          break;
        case '2':
        case '1':
        case 'R':
          task.redBox = true;
          this.onTaskActionChange('red', task);
          break;
        default:
          this.onTaskActionChange('grey', task);
          break;
      }
    }
  }

  onTreadDepthChange(task: any) {
    var treadDepth = parseInt(task.treadDepth);

    if (treadDepth > 32) {
      task.treadDepth = 32;
    }
    if (treadDepth >= this.treadDepthSettings.greenTreadDepth) {
      task.greenBox = true;
      this.onTaskActionChange('green', task);
    } else if (treadDepth >= this.treadDepthSettings.yellowTreadDepth) {
      task.yellowBox = true;
      this.onTaskActionChange('yellow', task);
    } else if (treadDepth >= this.treadDepthSettings.redTreadDepth) {
      task.redBox = true;
      this.onTaskActionChange('red', task);
    } else {
      this.onTaskActionChange('grey', task);
    }
  }

  onOilLifeChange(task: any) {
    if (task.oilLife > 100) {
      task.oilLife = 100;
    }
    var categoryIndex = this.inspection.categories.findIndex(function (el: any) {
      return el.categoryId === task.categoryId;
    });

    if (categoryIndex != -1) {
      var taskIndex = this.inspection.categories[categoryIndex].tasks.findIndex(function (el: any) {
        return (
          el.gmInspectionId === 'REMAININING_ENGINE_OIL_LIFE' || el.inspectionId === 'Q_REMAININING_ENGINE_OIL_LIFE'
        );
      });

      if (taskIndex != -1) {
        if (task.oilLife > 20) {
          this.inspection.categories[categoryIndex].tasks[taskIndex].greenBox = true;
          task.greenBox = true;
          this.onTaskActionChange('green', this.inspection.categories[categoryIndex].tasks[taskIndex]);
          this.onTaskActionChange('green', task);
        } else if (task.oilLife > 5) {
          this.inspection.categories[categoryIndex].tasks[taskIndex].yellowBox = true;
          task.yellowBox = true;
          this.onTaskActionChange('yellow', this.inspection.categories[categoryIndex].tasks[taskIndex]);
          this.onTaskActionChange('yellow', task);
        } else if (task.oilLife > 0 || task.oilLife === 0) {
          this.inspection.categories[categoryIndex].tasks[taskIndex].redBox = true;
          task.redBox = true;
          this.onTaskActionChange('red', this.inspection.categories[categoryIndex].tasks[taskIndex]);
          this.onTaskActionChange('red', task);
        } else {
          this.inspection.categories[categoryIndex].tasks[taskIndex].greyBox = true;
          task.greyBox = true;
          this.onTaskActionChange('grey', this.inspection.categories[categoryIndex].tasks[taskIndex]);
          this.onTaskActionChange('grey', task);
        }
      }
    }
    this.inspectionBehaviourSubject.next(this.inspection);
  }

  omitOilLife(task: any) {
    task.oilLife = null;
    this.onOilLifeChange(task);
  }

  omitDisplayVoltage(inspection: any, task: any) {
    task.batteryVoltage = null;
    this.onBatteryVoltageChange(inspection, task);
  }

  checkTireSealant(task: any) {
    if (task.vehicleSealantExpirationDate) {
      var sealantExpirationDate = new Date(task.vehicleSealantExpirationDate);
      var inspectionDate = new Date(this.inspection.inspectionDate);
      sealantExpirationDate.setHours(0, 0, 0, 0);
      inspectionDate.setHours(0, 0, 0, 0);
      if (Date.parse(inspectionDate.toString()) >= Date.parse(sealantExpirationDate.toString())) {
        task.redBox = true;
        this.onTaskActionChange('red', task);
      } else {
        task.greenBox = true;
        this.onTaskActionChange('green', task);
      }
    } else {
      task.greyBox = true;
      this.onTaskActionChange('grey', task);
    }
    this.inspectionBehaviourSubject.next(this.inspection);
  }

  onBatteryVoltageChange(inspection: any, task: any) {
    const inspectionTypeId = inspection.inspectionTypeId;
    this.apiService
      .get(`/v/2/vip/vehicle-inspection-thresholds/${inspectionTypeId}/BATTERY_VOLTAGE`)
      .toPromise()
      .then((response: any) => {
        const voltageThresholds = response;
        if (task.batteryVoltage > voltageThresholds.maxValue) {
          task.batteryVoltage = voltageThresholds.maxValue;
        }
        if (task.batteryVoltage) {
          const decimalIndex = task.batteryVoltage.toString().indexOf('.');
          if (decimalIndex > 0) {
            const decimalPlaces = task.batteryVoltage.toString().substring(decimalIndex);
            if (decimalPlaces.length > 3) {
              const batteryVoltage = task.batteryVoltage.toString().substring(0, decimalIndex + 3);
              task.batteryVoltage = parseFloat(batteryVoltage);
            }
          }
        }

        const categoryIndex = this.inspection.categories.findIndex((el: any) => {
          return el.categoryId === task.categoryId;
        });

        if (categoryIndex !== -1) {
          const taskIndex = this.inspection.categories[categoryIndex].tasks.findIndex((el: any) => {
            return (
              el.gmInspectionId === 'BATTERY_CONDITION' ||
              el.inspectionId === 'Q_BATTERY_CONDITION' ||
              el.inspectionId === 'HYU_BATTERY_CONDITION'
            );
          });

          if (taskIndex !== -1) {
            if (task.batteryVoltage >= voltageThresholds.greenThresholdValue) {
              this.inspection.categories[categoryIndex].tasks[taskIndex].greenBox = true;
              task.greenBox = true;
              this.onTaskActionChange('green', this.inspection.categories[categoryIndex].tasks[taskIndex]);
              this.onTaskActionChange('green', task);
            } else if (task.batteryVoltage >= voltageThresholds.yellowThresholdValue) {
              this.inspection.categories[categoryIndex].tasks[taskIndex].yellowBox = true;
              task.yellowBox = true;
              this.onTaskActionChange('yellow', this.inspection.categories[categoryIndex].tasks[taskIndex]);
              this.onTaskActionChange('yellow', task);
            } else if (task.batteryVoltage >= voltageThresholds.redThresholdValue && task.batteryVoltage !== null) {
              this.inspection.categories[categoryIndex].tasks[taskIndex].redBox = true;
              task.redBox = true;
              this.onTaskActionChange('red', this.inspection.categories[categoryIndex].tasks[taskIndex]);
              this.onTaskActionChange('red', task);
            } else {
              this.inspection.categories[categoryIndex].tasks[taskIndex].greyBox = true;
              task.greyBox = true;
              this.onTaskActionChange('grey', this.inspection.categories[categoryIndex].tasks[taskIndex]);
              this.onTaskActionChange('grey', task);
            }
          }
        }
        this.inspectionBehaviourSubject.next(this.inspection);
      });
  }

  passAllTasks(activeTab: any) {
    if (this.inspection.categories) {
      this.inspection.categories.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            if (activeTab === 0 || (activeTab === 1 && task.walkAround === 1)) {
              task.greenBox = true;
              this.onTaskActionChange('green', task);
            }
          }
        }, this);
      }, this);
    }

    if (this.inspection.tires) {
      this.inspection.tires.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            if (activeTab === 0 || (activeTab === 1 && task.walkAround === 1)) {
              task.greenBox = true;
              this.onTaskActionChange('green', task);
            }
          }
        }, this);
      }, this);
    }

    if (this.inspection.tireOtherCategories) {
      this.inspection.tireOtherCategories.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            if (activeTab === 0 || (activeTab === 1 && task.walkAround === 1)) {
              task.greenBox = true;
              this.onTaskActionChange('green', task);
            }
          }
        }, this);
      }, this);
    }

    this.updateTaskCounts();
  }

  passAllIncompleteTasks(activeTab: any) {
    this.unsaved = true;
    if (this.inspection.categories) {
      this.inspection.categories.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            if (activeTab === 0 || (activeTab === 1 && task.walkAround === 1)) {
              if (!task.greenBox && !task.redBox && !task.yellowBox && !task.greyBox) {
                task.greenBox = true;
              }
            }
          }
        }, this);
      }, this);
    }

    if (this.inspection.tires) {
      this.inspection.tires.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            if (activeTab === 0 || (activeTab === 1 && task.walkAround === 1)) {
              if (!task.greenBox && !task.redBox && !task.yellowBox && !task.greyBox) {
                task.greenBox = true;
              }
            }
          }
        }, this);
      }, this);
    }
    if (this.inspection.tireOtherCategories) {
      this.inspection.tireOtherCategories.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            if (activeTab === 0 || (activeTab === 1 && task.walkAround === 1)) {
              if (!task.greenBox && !task.redBox && !task.yellowBox && !task.greyBox) {
                task.greenBox = true;
              }
            }
          }
        }, this);
      }, this);
    }

    this.updateTaskCounts();
  }

  clearAllSelections() {
    this.unsaved = true;
    if (this.inspection.categories) {
      this.inspection.categories.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            task.greenBox = false;
            task.redBox = false;
            task.yellowBox = false;
            task.greyBox = false;
          }
        }, this);
      }, this);
    }
    if (this.inspection.tires) {
      this.inspection.tires.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            task.greenBox = false;
            task.redBox = false;
            task.yellowBox = false;
            task.greyBox = false;
          }
        }, this);
      }, this);
    }

    if (this.inspection.tireOtherCategories) {
      this.inspection.tireOtherCategories.forEach(function (element: any) {
        element.tasks.forEach(function (task: any) {
          if (!this.disabledTaskCheck(task)) {
            task.greenBox = false;
            task.redBox = false;
            task.yellowBox = false;
            task.greyBox = false;
          }
        }, this);
      }, this);
    }

    this.updateTaskCounts();
  }

  getYellowActionCount() {
    return new Promise((resolve, reject) => {
      var yellowActions: any = [];
      if (this.inspection.categories) {
        this.inspection.categories.forEach(function (element: any) {
          element.tasks.forEach(function (task: any) {
            if (task.yellowBox === true && task.yellowCondition !== '0' && task.inserted === false) {
              yellowActions.push(task);
            }
          }, this);
        }, this);
      }

      if (this.inspection.tires) {
        this.inspection.tires.forEach(function (element: any) {
          element.tasks.forEach(function (task: any) {
            if (task.yellowBox === true && task.yellowCondition !== '0' && task.inserted === false) {
              yellowActions.push(task);
            }
          }, this);
        }, this);
      }

      resolve(yellowActions);
    });
  }

  /**
   * Helper function to determine if a task is considered "disabled" when executing certain functionality within inspections
   * (All Green, All Unslected Green, Reset). A task is considered "disabled" if it requires a dropdown selection or manual data entry.
   *
   * @param task object containing inspection task data
   * @returns true if "disabled", false otherwise
   */
  disabledTaskCheck(task: any) {
    if (task.gmInspectionId !== null) {
      switch (task.gmInspectionId) {
        case 'TIRES_LF':
        case 'TIRES_LR':
        case 'TIRES_RF':
        case 'TIRES_RR':
        case 'TIRES_SPARE':
        case 'CHK_BREAKS_LININGS_LF':
        case 'CHK_BREAKS_LININGS_LR':
        case 'CHK_BREAKS_LININGS_RF':
        case 'CHK_BREAKS_LININGS_RR':
        case 'CHK_BREAKS_DRUM_LR':
        case 'CHK_BREAKS_DRUM_RR':
        case 'REMAININING_ENGINE_OIL_LIFE':
        case 'OIL_LIFE':
        case 'BATTERY_VOLTAGE':
        case 'TIRE_SEALANT_EXPIRATION':
          return true;
        case 'BATTERY_CONDITION':
          if (
            ['GM MPVI - Standard', 'GM MPVI - Express', 'GM MPVI - Standard(EV)', 'GM MPVI - Express(EV)'].includes(
              this.inspection.name
            )
          ) {
            return false;
          } else {
            return true;
          }
        default:
          return false;
      }
    }
    if (task.inspectionId !== null) {
      switch (task.inspectionId) {
        case 'Q_TIRES_LF':
        case 'Q_TIRES_LR':
        case 'Q_TIRES_RF':
        case 'Q_TIRES_RR':
        case 'Q_TIRES_SPARE':
        case 'Q_CHK_BREAKS_LININGS_LF':
        case 'Q_CHK_BREAKS_LININGS_LR':
        case 'Q_CHK_BREAKS_LININGS_RF':
        case 'Q_CHK_BREAKS_LININGS_RR':
        case 'Q_REMAININING_ENGINE_OIL_LIFE':
        case 'Q_OIL_LIFE':
        case 'Q_BATTERY_CONDITION':
        case 'Q_BATTERY_VOLTAGE':
        case 'HYU_BRAKES_LF':
        case 'HYU_BRAKES_LR':
        case 'HYU_BRAKES_RF':
        case 'HYU_BRAKES_RR':
        case 'HYU_BATTERY_CONDITION':
        case 'HYU_BATTERY_VOLTAGE':
        case 'HYU_TIRES_LF':
        case 'HYU_TIRES_LR':
        case 'HYU_TIRES_RF':
        case 'HYU_TIRES_RR':
          return true;
        default:
          return false;
      }
    }
    // eslint-disable-next-line max-len
    //This section is for all other inspections, as tread and depth should always be controlled and never follow all check rules.
    else {
      if (task.displayTireTread || task.displayBrakeMeasurement) {
        return true;
      } else {
        return false;
      }
    }
  }

  savingCheck() {
    return new Promise((resolve, reject) => {
      var categoryCounter = 0;
      var walkAroundCategoryCounter = 0;
      var categoryId = 0;
      if (this.inspection.categories) {
        this.inspection.categories.forEach(function (element: any) {
          element.tasks.forEach(function (task: any) {
            categoryId = task.categoryId;
            if (!task.greenBox && !task.redBox && !task.yellowBox && !task.greyBox) {
              this.requiredTasks[task.taskName] = true;
              categoryCounter = categoryCounter + 1;
              if (task.walkAround === 1) {
                this.walkAroundRequiredTasks[task.taskName] = true;
                walkAroundCategoryCounter = walkAroundCategoryCounter + 1;
              }
            } else {
              this.requiredTasks[task.taskName] = false;
              if (task.walkAround === 1) {
                this.walkAroundRequiredTasks[task.taskName] = false;
              }
            }
          }, this);
          if (categoryCounter > 0) {
            this.requiredCategory[categoryId] = true;
          } else {
            this.requiredCategory[categoryId] = false;
          }
          if (walkAroundCategoryCounter > 0) {
            this.walkAroundRequiredCategory[categoryId] = true;
          } else {
            this.walkAroundRequiredCategory[categoryId] = false;
          }
          categoryCounter = 0;
          walkAroundCategoryCounter = 0;
          categoryId = 0;
        }, this); // jshint ignore:line
      }
      if (this.inspection.tires) {
        this.inspection.tires.forEach(function (element: any) {
          element.tasks.forEach(function (task: any) {
            categoryId = task.categoryId;
            if (!task.greenBox && !task.redBox && !task.yellowBox && !task.greyBox) {
              this.requiredTasks[task.taskName] = true;
              categoryCounter = categoryCounter + 1;
              if (task.walkAround === 1) {
                this.walkAroundRequiredTasks[task.taskName] = true;
                walkAroundCategoryCounter = walkAroundCategoryCounter + 1;
              }
            } else {
              this.requiredTasks[task.taskName] = false;
              if (task.walkAround === 1) {
                this.walkAroundRequiredTasks[task.taskName] = false;
              }
            }
          }, this);
          if (categoryCounter > 0) {
            this.requiredCategory[categoryId] = true;
          } else {
            this.requiredCategory[categoryId] = false;
          }
          if (walkAroundCategoryCounter > 0) {
            this.walkAroundRequiredCategory[categoryId] = true;
          } else {
            this.walkAroundRequiredCategory[categoryId] = false;
          }
          categoryCounter = 0;
          walkAroundCategoryCounter = 0;
          categoryId = 0;
        }, this); // jshint ignore:line
      }
      if (this.inspection.tireOtherCategories) {
        this.inspection.tireOtherCategories.forEach(function (element: any) {
          element.tasks.forEach(function (task: any) {
            categoryId = task.categoryId;
            if (!task.greenBox && !task.redBox && !task.yellowBox && !task.greyBox) {
              this.requiredTasks[task.taskName] = true;
              categoryCounter = categoryCounter + 1;
              if (task.walkAround === 1) {
                this.walkAroundRequiredTasks[task.taskName] = true;
                walkAroundCategoryCounter = walkAroundCategoryCounter + 1;
              }
            } else {
              this.requiredTasks[task.taskName] = false;
              if (task.walkAround === 1) {
                this.walkAroundRequiredTasks[task.taskName] = false;
              }
            }
          }, this);
          if (categoryCounter > 0) {
            this.requiredCategory[categoryId] = true;
          } else {
            this.requiredCategory[categoryId] = false;
          }
          if (walkAroundCategoryCounter > 0) {
            this.walkAroundRequiredCategory[categoryId] = true;
          } else {
            this.walkAroundRequiredCategory[categoryId] = false;
          }
          categoryCounter = 0;
          walkAroundCategoryCounter = 0;
          categoryId = 0;
        }, this); // jshint ignore:line
      }
      if (Object.values(this.requiredTasks).includes(true)) {
        this.incomplete = true;
      } else {
        this.incomplete = false;
      }

      if (Object.values(this.walkAroundRequiredTasks).includes(true)) {
        this.walkAroundIncomplete = true;
      } else {
        this.walkAroundIncomplete = false;
      }

      resolve(null);
    });
  }

  leavingInspection() {
    return new Promise((resolve, reject) => {
      if (this.unsaved) {
        const dialogData: TwoActionDialogData = {
          actionTwoCaption: 'No',
          actionOneCaption: 'Yes',
          content: 'You have unsaved changes. Proceed without saving?',
          title: 'Unsaved Changes',
        };

        const dialogRef = this.dialog.open(TwoActionDialogComponent, {
          width: '400px',
          data: dialogData,
        });

        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            this.inspection = this.inspectionBackup;
            this.inspectionBehaviourSubject.next(this.inspection);
            this.unsaved = false;
            resolve(true);
          } else {
            resolve(false);
          }
        });

        // var confirm = $mdDialog
        //   .confirm()
        //   .title('Unsaved changes')
        //   .textContent('You have unsaved changes. Proceed without saving?')
        //   .targetEvent()
        //   .ok('yes')
        //   .cancel('no');

        // $mdDialog.show(confirm).then(
        //   function() {
        //     this.inspection = this.inspectionBackup;
        //     this.unsaved = false;
        //     resolve(true);
        //   },
        //   function() {
        //     resolve(false);
        //   }
        // );
      } else {
        resolve(true);
      }
    });
  }

  getQuote(workOrderId: number) {
    return this.apiService
      .get('/v/2/vip/quote/' + workOrderId, {})
      .toPromise()
      .then(function (response) {
        return response;
      });
  }

  getBodyReport(workOrderId: any) {
    return this.apiService
      .get('/v/1/fixed-operations/work-orders/' + workOrderId, {})
      .toPromise()
      .then(function (workOrder: WorkOrder | null) {
        return {
          data: workOrder?.bodyReportData ? workOrder.bodyReportData : '[]',
          workOrder: workOrder ?? null,
        };
      });
  }

  saveBodyReport(workOrder: WorkOrder, bodyReport: any, signature: string, vehicleDirty: boolean) {
    workOrder.bodyReportData =
      bodyReport === null && signature === null && vehicleDirty === null
        ? null
        : JSON.stringify({
            bodyReport: bodyReport,
            signature: signature,
            vehicleDirty: vehicleDirty,
          });
    return this.apiService.put('v/1/fixed-operations/work-orders/' + workOrder.id, workOrder).toPromise();
  }

  getInspectionTypes(oem: OemRooftopLink, fuelType: string) {
    const vehicleInspectionTypeQueryParameters: VehicleInspectionTypeQueryParameters =
      new VehicleInspectionTypeQueryParameters({
        oemId: oem.associateId,
        expired: false,
        fuelType: fuelType,
      });

    return this.apiService
      .get('v/1/vip/vehicle-inspection-types', { params: vehicleInspectionTypeQueryParameters })
      .toPromise()
      .then(function (response) {
        const inspectionTypes = response;
        return inspectionTypes;
      });
  }

  getOnstar(vehicleId: any) {
    return this.apiService
      .get('/v/2/vip/onStar', {
        params: { vehicleId: vehicleId },
      })
      .toPromise()
      .then(function (response) {
        return response;
      });
  }

  getTreadDepthSettings(oemrtId: any) {
    return this.apiService
      .get('/v/2/vip/tread-depth-settings/' + oemrtId)
      .toPromise()
      .then(function (response) {
        return response;
      });
  }

  getBatteryVoltageThresholds(inspectionTypeId: any) {
    return this.apiService
      .get('/v/2/vip/vehicle-inspection-thresholds/' + inspectionTypeId + '/BATTERY_VOLTAGE')
      .toPromise()
      .then(function (response) {
        return response;
      });
  }

  getVcrReportData(workOrderId: any, quoteId: any) {
    let self = this;
    combineLatest([
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((emp) => emp != null),
        take(1)
      ),
      this.authenticationStateService.selectQuorumDestinationId().pipe(
        filter((destinationId) => !!destinationId),
        take(1)
      ),
    ])
      .pipe(
        take(1),
        switchMap(([employee, destinationId]) =>
          this.apiService.get(`/v/1/system/settings/marketing/stores/${destinationId}`).pipe(
            map((settings: any) => {
              // Per-rooftop entry wins over the dealer-default. Falls back to '' if neither exists.
              const rooftopUrl: string | undefined = settings?.dealerLogosByRooftop?.[String(employee.rooftopId)]?.url;
              const defaultUrl: string | undefined = settings?.dealerLogo?.url;
              this.dealershipLogo = rooftopUrl ?? defaultUrl ?? '';
            })
          )
        )
      )
      .subscribe();

    return this.apiService
      .get(`/v/2/vip/inspections/vcr-report/${workOrderId}/${quoteId}`)
      .toPromise()
      .then(function (response) {
        self.report = response;

        // START: this section is used for the inspection printout
        const brakeTaskNames = new Set([
          'Driver Front Brake',
          'Driver Rear Brake',
          'Passenger Front Brake',
          'Passenger Rear Brake',
          'Brakes - RR',
          'Brakes - RF',
          'Brakes - LR',
          'Brakes - LF',
          'Driver Rear Brake Drum',
          'Passenger Rear Brake Drum',
        ]);

        const tasks: any[] = response?.tasks ?? [];
        self.brakeMeasurementTasks$.next(
          tasks.filter((task: any) => brakeTaskNames.has(task.TaskName) && task.Color !== 'Grey')
        );

        self.filteredTasks$.next(tasks.filter((task: any) => !brakeTaskNames.has(task.TaskName)));
        // END: this section is used for the inspection printout

        return response;
      });
  }

  deleteInspection(workOrderId: any) {
    return this.apiService
      .delete('v/2/vip/inspections/' + workOrderId)
      .toPromise()
      .then(function (response) {
        return response;
      });
  }

  getSystemControlValue(oemrtId: number, sysclConstantRef: string): Observable<string> {
    const params = new SystemControlValueQueryParameters({
      constantReference: sysclConstantRef,
      rooftopId: oemrtId.toString(),
    });

    return this.apiService
      .get<SystemControlValue[]>(`v/1/system/system-control-values`, {
        params: params,
      })
      .pipe(
        map((sct: SystemControlValue[]) => {
          return sct[0].value;
        })
      );
  }

  deleteBodyReport() {
    this.bodyReportObject.bodyReportElements = [];
    this.bodyReportObject.signature = null;
    this.bodyReportObject.vehicleDirty = false;
  }

  open(config: BodyReportDamageDialogConfig = {}) {
    // Override default configuration
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };

    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(dialogConfig);

    // Instantiate remote control
    const dialogRef = new BodyReportDamageOverlayRef(overlayRef);

    const overlayComponent = this.attachDialogContainer(overlayRef, dialogConfig, dialogRef);
    dialogRef.componentInstance = overlayComponent;
    overlayRef.backdropClick().subscribe((_) => dialogRef.close());

    return dialogRef;
  }

  private createOverlay(config: BodyReportDamageDialogConfig) {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  private attachDialogContainer(
    overlayRef: OverlayRef,
    config: BodyReportDamageDialogConfig,
    dialogRef: BodyReportDamageOverlayRef
  ) {
    const injector = this.createInjector(config, dialogRef);

    const containerPortal = new ComponentPortal(BodyReportDamageComponent, null, injector);
    const containerRef: ComponentRef<BodyReportDamageComponent> = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private createInjector(config: BodyReportDamageDialogConfig, dialogRef: BodyReportDamageOverlayRef): PortalInjector {
    const injectionTokens = new WeakMap();

    injectionTokens.set(BodyReportDamageOverlayRef, dialogRef);
    injectionTokens.set(BODY_REPORT_DAMAGE_DATA, config.selectedElement);

    return new PortalInjector(this.injector, injectionTokens);
  }

  private getOverlayConfig(config: BodyReportDamageDialogConfig): OverlayConfig {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(config.originElement)
      .withPositions(this.getPositions())
      .withPush(true);
    // .connectedTo(config.originElement, { originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'top' });
    //const positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();

    const overlayConfig = new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy,
    });

    return overlayConfig;
  }

  private getPositions(): ConnectionPositionPair[] {
    return [
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'top',
      },
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'bottom',
      },
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'bottom',
      },
      // {
      //   originX: 'end',
      //   originY: 'bottom',
      //   overlayX: 'start',
      //   overlayY: 'top',
      // },
      // {
      //   originX: 'start',
      //   originY: 'bottom',
      //   overlayX: 'end',
      //   overlayY: 'top',
      // },
    ];
  }

  displaySaviIcon(displaySaviIconFlags: { gmOem: boolean; vehicleDiagnosticsEnabled: boolean }, task: any) {
    if (displaySaviIconFlags && displaySaviIconFlags.gmOem && displaySaviIconFlags.vehicleDiagnosticsEnabled) {
      return combineLatest([
        this.svcVehicleDiagnosticsRootFacade.recentServiceVisitBasedOnVin$.pipe(
          filter((latestServiceVisit) => latestServiceVisit != null)
        ),
        this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitMpvi$.pipe(filter((mpvi) => mpvi.length > 0)),
      ]).pipe(
        take(1),
        map(([latestServiceVisit, linkedServiceVisitMpvi]) => {
          if (this.vehicleDiagnosticsId === latestServiceVisit.serviceVisitId) {
            return this.compareGmMpviData(task, linkedServiceVisitMpvi);
          }
          return false;
        })
      );
    }
    return of(false);
  }

  compareGmMpviData(task: any, linkedServiceVisitMpvi: GMServiceVisitVehicleData[]) {
    if (task.gmInspectionId !== null) {
      switch (task.gmInspectionId) {
        case 'TIRES_RR':
          return Math.round(task.psiIn) ===
            Math.round(
              Number(
                linkedServiceVisitMpvi[
                  linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE RR')
                ].value
              )
            )
            ? true
            : false;
        case 'TIRES_LR':
          return Math.round(task.psiIn) ===
            Math.round(
              Number(
                linkedServiceVisitMpvi[
                  linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE LR')
                ].value
              )
            )
            ? true
            : false;
        case 'TIRES_LF':
          return Math.round(task.psiIn) ===
            Math.round(
              Number(
                linkedServiceVisitMpvi[
                  linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE LF')
                ].value
              )
            )
            ? true
            : false;
        case 'TIRES_RF':
          return Math.round(task.psiIn) ===
            Math.round(
              Number(
                linkedServiceVisitMpvi[
                  linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE RF')
                ].value
              )
            )
            ? true
            : false;
        default:
          break;
      }
    } else if (task.inspectionId != null) {
      //InspectionId is used for all non one page inpections. Q/HYU etc. Currently no reason to add Hyundai.
      switch (task.inspectionId) {
        case 'Q_TIRES_RR':
          return Math.round(task.psiIn) ===
            Math.round(
              Number(
                linkedServiceVisitMpvi[
                  linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE RR')
                ].value
              )
            )
            ? true
            : false;
        case 'Q_TIRES_LR':
          return Math.round(task.psiIn) ===
            Math.round(
              Number(
                linkedServiceVisitMpvi[
                  linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE LR')
                ].value
              )
            )
            ? true
            : false;
        case 'Q_TIRES_LF':
          return Math.round(task.psiIn) ===
            Math.round(
              Number(
                linkedServiceVisitMpvi[
                  linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE LF')
                ].value
              )
            )
            ? true
            : false;
        case 'Q_TIRES_RF':
          return Math.round(task.psiIn) ===
            Math.round(
              Number(
                linkedServiceVisitMpvi[
                  linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE RF')
                ].value
              )
            )
            ? true
            : false;
        default:
          break;
      }
    }

    if (task.gmInspectionId === 'OIL_LIFE' || task.inspectionId === 'Q_OIL_LIFE') {
      return Math.round(task.oilLife) ===
        Math.round(
          Number(
            linkedServiceVisitMpvi[linkedServiceVisitMpvi.findIndex((mpvi: any) => mpvi.displayName === 'OIL LIFE %')]
              .value
          )
        )
        ? true
        : false;
    }
  }
  //Function is used to determine if we should overwrite the existing SaviData Fields
  gmSaviOverwriteDataCheck(linkedServiceVisitMpviData: GMServiceVisitVehicleData[]) {
    let mpviAreEqualCount: number = 0;
    if (this.inspection.tires) {
      this.inspection.tires.forEach((tires: any) => {
        tires.tasks.forEach((task: any) => {
          this.compareGmMpviData(task, linkedServiceVisitMpviData) ? mpviAreEqualCount++ : mpviAreEqualCount;
        });
      });
    }
    let oilCategoryIndex = this.inspection.categories.findIndex(
      (category: any) => category.categoryName === 'Oil Life'
    );
    if (oilCategoryIndex != -1) {
      this.inspection.categories[oilCategoryIndex].tasks.forEach((task: any) => {
        this.compareGmMpviData(task, linkedServiceVisitMpviData) ? mpviAreEqualCount++ : mpviAreEqualCount;
      });
    }
    return mpviAreEqualCount === 0 ? true : false;
  }

  setMpviData(linkedServiceVisitMpviData: GMServiceVisitVehicleData[], serviceVisitId: string) {
    if (this.inspection.tires) {
      this.inspection.tires.forEach((tires: any) => {
        tires.tasks.forEach((task: any) => {
          //GM inspections have gm inspectionId, which is used as static_key for all tasks.
          //Currently we need to assume displayname is a static key to find matching value in mpvi data.
          if (task.gmInspectionId !== null) {
            switch (task.gmInspectionId) {
              case 'TIRES_RR':
                task.psiIn = Math.round(
                  Number(
                    linkedServiceVisitMpviData[
                      linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE RR')
                    ].value
                  )
                );
                break;
              case 'TIRES_LR':
                task.psiIn = Math.round(
                  Number(
                    linkedServiceVisitMpviData[
                      linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE LR')
                    ].value
                  )
                );
                break;
              case 'TIRES_LF':
                task.psiIn = Math.round(
                  Number(
                    linkedServiceVisitMpviData[
                      linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE LF')
                    ].value
                  )
                );
                break;
              case 'TIRES_RF':
                task.psiIn = Math.round(
                  Number(
                    linkedServiceVisitMpviData[
                      linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE RF')
                    ].value
                  )
                );
                break;
              default:
                break;
            }
          } else if (task.inspectionId != null) {
            //InspectionId is used for all non one page inpections. Q/HYU etc. Currently no reason to add Hyundai.
            switch (task.inspectionId) {
              case 'Q_TIRES_RR':
                task.psiIn = Math.round(
                  Number(
                    linkedServiceVisitMpviData[
                      linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE RR')
                    ].value
                  )
                );
                break;
              case 'Q_TIRES_LR':
                task.psiIn = Math.round(
                  Number(
                    linkedServiceVisitMpviData[
                      linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE LR')
                    ].value
                  )
                );
                break;
              case 'Q_TIRES_LF':
                task.psiIn = Math.round(
                  Number(
                    linkedServiceVisitMpviData[
                      linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE LF')
                    ].value
                  )
                );
                break;
              case 'Q_TIRES_RF':
                task.psiIn = Math.round(
                  Number(
                    linkedServiceVisitMpviData[
                      linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'TIRE PRESSURE RF')
                    ].value
                  )
                );
                break;
              default:
                break;
            }
          }
        });
      });
    }
    //Oil Category is special, as one task controls another. We set value of Oil life then onOilLifeChange selects green on another task
    let oilCategoryIndex = this.inspection.categories.findIndex(
      (category: any) => category.categoryName === 'Oil Life'
    );
    if (oilCategoryIndex != -1) {
      this.inspection.categories[oilCategoryIndex].tasks.forEach((task: any) => {
        if (task.gmInspectionId === 'OIL_LIFE' || task.inspectionId === 'Q_OIL_LIFE') {
          task.oilLife = Math.round(
            Number(
              linkedServiceVisitMpviData[
                linkedServiceVisitMpviData.findIndex((mpvi: any) => mpvi.displayName === 'OIL LIFE %')
              ].value
            )
          );
          this.onOilLifeChange(task);
        }
      });
    }
  }

  updateTaskCounts() {
    const inspectionTaskCount: {
      inspectionTabCategoryTotals: { [categoryName: string]: { actioned: number; omitted: number; total: number } };
      walkAroundTabCategoryTotals: { [categoryName: string]: { actioned: number; omitted: number; total: number } };
      totalInspectionTaskCount: number;
      totalInspectionActionedCount: number;
      totalInspectionOmittedCount: number;
      totalWalkAroundTaskCount: number;
      totalWalkAroundActionedCount: number;
      totalWalkAroundOmittedCount: number;
    } = {
      inspectionTabCategoryTotals: {},
      walkAroundTabCategoryTotals: {},
      totalInspectionTaskCount: 0,
      totalInspectionActionedCount: 0,
      totalInspectionOmittedCount: 0,
      totalWalkAroundTaskCount: 0,
      totalWalkAroundActionedCount: 0,
      totalWalkAroundOmittedCount: 0,
    };

    if (this.inspection.categories) {
      for (let i = 0; i < this.inspection.categories.length; i++) {
        const categoryName: string = this.inspection.categories[i].categoryName;
        let walkAroundTaskCount = 0;
        let walkAroundActionedCount = 0;
        let walkAroundOmittedCount = 0;
        let inspectionActionedCount = 0;
        let inspectionOmittedCount = 0;

        for (let j = 0; j < this.inspection.categories[i].tasks.length; j++) {
          if (this.inspection.categories[i].tasks[j].walkAround === 1) {
            walkAroundTaskCount++;
            if (
              this.inspection.categories[i].tasks[j].greenBox ||
              this.inspection.categories[i].tasks[j].redBox ||
              this.inspection.categories[i].tasks[j].yellowBox
            ) {
              walkAroundActionedCount++;
              inspectionTaskCount.totalWalkAroundActionedCount++;
            }
            if (this.inspection.categories[i].tasks[j].greyBox) {
              walkAroundOmittedCount++;
              inspectionTaskCount.totalWalkAroundOmittedCount++;
            }
          }
          if (
            this.inspection.categories[i].tasks[j].greenBox ||
            this.inspection.categories[i].tasks[j].redBox ||
            this.inspection.categories[i].tasks[j].yellowBox
          ) {
            inspectionActionedCount++;
            inspectionTaskCount.totalInspectionActionedCount++;
          }
          if (this.inspection.categories[i].tasks[j].greyBox) {
            inspectionOmittedCount++;
            inspectionTaskCount.totalInspectionOmittedCount++;
          }
        }
        inspectionTaskCount.inspectionTabCategoryTotals[categoryName] = {
          actioned: inspectionActionedCount,
          omitted: inspectionOmittedCount,
          total: this.inspection.categories[i].tasks.length,
        };
        inspectionTaskCount.walkAroundTabCategoryTotals[categoryName] = {
          actioned: walkAroundActionedCount,
          omitted: walkAroundOmittedCount,
          total: walkAroundTaskCount,
        };
        inspectionTaskCount.totalInspectionTaskCount += this.inspection.categories[i].tasks.length;
        inspectionTaskCount.totalWalkAroundTaskCount += walkAroundTaskCount;
      }
    }

    if (this.inspection.tireOtherCategories) {
      for (let i = 0; i < this.inspection.tireOtherCategories.length; i++) {
        const categoryName: string = this.inspection.tireOtherCategories[i].categoryName;
        let walkAroundTaskCount = 0;
        let walkAroundActionedCount = 0;
        let walkAroundOmittedCount = 0;
        let inspectionActionedCount = 0;
        let inspectionOmittedCount = 0;

        for (let j = 0; j < this.inspection.tireOtherCategories[i].tasks.length; j++) {
          if (this.inspection.tireOtherCategories[i].tasks[j].walkAround === 1) {
            walkAroundTaskCount++;
            if (
              this.inspection.tireOtherCategories[i].tasks[j].greenBox ||
              this.inspection.tireOtherCategories[i].tasks[j].redBox ||
              this.inspection.tireOtherCategories[i].tasks[j].yellowBox
            ) {
              walkAroundActionedCount++;
              inspectionTaskCount.totalWalkAroundActionedCount++;
            }
            if (this.inspection.tireOtherCategories[i].tasks[j].greyBox) {
              walkAroundOmittedCount++;
              inspectionTaskCount.totalWalkAroundOmittedCount++;
            }
          }
          if (
            this.inspection.tireOtherCategories[i].tasks[j].greenBox ||
            this.inspection.tireOtherCategories[i].tasks[j].redBox ||
            this.inspection.tireOtherCategories[i].tasks[j].yellowBox
          ) {
            inspectionActionedCount++;
            inspectionTaskCount.totalInspectionActionedCount++;
          }
          if (this.inspection.tireOtherCategories[i].tasks[j].greyBox) {
            inspectionOmittedCount++;
            inspectionTaskCount.totalInspectionOmittedCount++;
          }
        }
        inspectionTaskCount.inspectionTabCategoryTotals[categoryName] = {
          actioned: inspectionActionedCount,
          omitted: inspectionOmittedCount,
          total: this.inspection.tireOtherCategories[i].tasks.length,
        };
        inspectionTaskCount.walkAroundTabCategoryTotals[categoryName] = {
          actioned: walkAroundActionedCount,
          omitted: walkAroundOmittedCount,
          total: walkAroundTaskCount,
        };
        inspectionTaskCount.totalInspectionTaskCount += this.inspection.tireOtherCategories[i].tasks.length;
        inspectionTaskCount.totalWalkAroundTaskCount += walkAroundTaskCount;
      }
    }

    let tiresWalkAroundActionedCount = 0;
    let tiresWalkAroundOmittedCount = 0;
    let tiresInspectionActionedCount = 0;
    let tiresInspectionOmittedCount = 0;

    for (let i = 0; i < this.inspection.tires[0].tasks.length; i++) {
      if (
        this.inspection.tires[0].tasks[i].greenBox ||
        this.inspection.tires[0].tasks[i].redBox ||
        this.inspection.tires[0].tasks[i].yellowBox
      ) {
        tiresWalkAroundActionedCount++;
        tiresInspectionActionedCount++;
        inspectionTaskCount.totalInspectionActionedCount++;
        inspectionTaskCount.totalWalkAroundActionedCount++;
      }
      if (this.inspection.tires[0].tasks[i].greyBox) {
        tiresWalkAroundOmittedCount++;
        tiresInspectionOmittedCount++;
        inspectionTaskCount.totalInspectionOmittedCount++;
        inspectionTaskCount.totalWalkAroundOmittedCount++;
      }
    }

    inspectionTaskCount.inspectionTabCategoryTotals['Tires'] = {
      actioned: tiresInspectionActionedCount,
      omitted: tiresInspectionOmittedCount,
      total: this.inspection.tires[0].tasks.length,
    };
    inspectionTaskCount.walkAroundTabCategoryTotals['Tires'] = {
      actioned: tiresWalkAroundActionedCount,
      omitted: tiresWalkAroundOmittedCount,
      total: this.inspection.tires[0].tasks.length,
    };

    inspectionTaskCount.totalInspectionTaskCount += this.inspection.tires[0].tasks.length;
    inspectionTaskCount.totalWalkAroundTaskCount += this.inspection.tires[0].tasks.length;

    //Combined layout: the dually positions render inside the Tires card without a heading of
    //their own, so their progress belongs in the Tires count. Only the displayed figures are
    //merged — the grand totals already counted them with the other categories.
    if (this.dualRearTiresEnabled) {
      const dualCategoryName = this.getDualRearTiresCategory()?.categoryName;

      if (dualCategoryName) {
        inspectionTaskCount.inspectionTabCategoryTotals['Tires'] = this.mergeCategoryTotals(
          inspectionTaskCount.inspectionTabCategoryTotals['Tires'],
          inspectionTaskCount.inspectionTabCategoryTotals[dualCategoryName]
        );
        inspectionTaskCount.walkAroundTabCategoryTotals['Tires'] = this.mergeCategoryTotals(
          inspectionTaskCount.walkAroundTabCategoryTotals['Tires'],
          inspectionTaskCount.walkAroundTabCategoryTotals[dualCategoryName]
        );
      }
    }

    this.inspectionTaskCount$.next(inspectionTaskCount);
  }

  private mergeCategoryTotals(
    target: { actioned: number; omitted: number; total: number },
    source: { actioned: number; omitted: number; total: number } | undefined
  ): { actioned: number; omitted: number; total: number } {
    if (!source) {
      return target;
    }

    return {
      actioned: target.actioned + source.actioned,
      omitted: target.omitted + source.omitted,
      total: target.total + source.total,
    };
  }
}
